import type { ApiResponse, Pagination } from '@/shared/types/api';
import { api } from '../../../shared/api/client';
import type {
  // HouseManager,
  // CreateManagerAssignmentData,
  // UpdateManagerAssignmentData,
  // ManagerStats,
  HouseFiltersType,
} from '../types';
import type { ResidentApiType } from '@/features/residents/api';

export interface House {
  id: string;
  name: string;
  type?: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  capacity: number;
  description?: string;
  rules?: string;
  amenities: string[];
  images: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  managerId: string;
  manager: {
    id: string;
    name: string;
    email: string;
  };
  residents?: ResidentApiType[];
  _count?: {
    residents: number;
  };
  // Additional properties from CreateHouseData
  bedrooms?: number;
  bathrooms?: number;
  area?: string;
  size?: string;
  builtYear?: string;
  isParkingAvailable?: boolean;
  isFurnished?: boolean;
  price?: number;
  currency?: string;
  status?: string;
  phoneNumber?: string;
}

export interface DropdownHouseListItem {
  id: string;
  name: string;
}

export interface CreateHouseData {
  name: string;
  type: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  description: string;
  capacity: number;
  bedrooms: number;
  bathrooms: number;
  area: string;
  size: string;
  builtYear: string;
  isParkingAvailable: boolean;
  isFurnished: boolean;
  price: number;
  currency: string;
  seats: number;
  status: 'ACTIVE' | 'INACTIVE' | 'DRAFT';
  amenities: string[];
  images: string[];
  listingDate: string;
  id?: string;
}

export interface HouseStats {
  occupancy: {
    current: number;
    capacity: number;
    rate: number;
  };
  revenue: {
    monthly: number;
    pending: number;
  };
  payments: {
    overdue: number;
    total: number;
  };
}

export interface OverviewStat {
  value: string | number;
  change: string;
  status: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
  label: string;
}

export interface HouseOverviewItem {
  id: string;
  name: string;
  images: string[];
  status: string;
  capacityPercentage: string;
  capacity: number;
  totalResident: number;
}

export interface HouseOverviewData {
  thisMonthEarning: OverviewStat;
  monthlyEarning: OverviewStat;
  totalResidents: OverviewStat;
  houses: HouseOverviewItem[];
}

// Define the base response type
// interface ApiResponse<T> {
//   success: boolean;
//   message: string;
//   data: T;
// }

// interface Pagination {
//   currentPage: number;
//   hasNextPage: boolean;
//   hasPrevPage: boolean;
//   nextPage: string | null;
//   prevPage: string | null;
//   perPage: number;
//   totalPages: number;
//   totalRecords: number;
// }

interface GetHousesResponse {
  houses: House[];
  pagination: Pagination;
}

// Define the specific house response type
interface GetHouseByIdResponse {
  house: House;
}
interface GetHouseListResponse {
  houses: House[];
}

export const houseApi = {
  // Get Houses with filters and pagination
  getHousesWithFilters: async (
    filters: HouseFiltersType,
  ): Promise<GetHousesResponse> => {
    const response = await api.get<ApiResponse<GetHousesResponse>>('/houses', {
      params: filters,
    });
    return response.data;
  },

  // Get Houses list for organization
  getHousesList: async (): Promise<DropdownHouseListItem[]> => {
    const response = await api.get<ApiResponse<GetHouseListResponse>>(
      '/houses/list',
    );
    return response.data?.houses;
  },

  // Get all houses
  getHouses: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    managerId?: string;
  }): Promise<{
    houses: House[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    try {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.search) searchParams.append('search', params.search);
      if (params?.managerId) searchParams.append('managerId', params.managerId);

      return api.get<{
        houses: House[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>(`/houses?${searchParams.toString()}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to fetch houses',
      );
    }
  },

  getHouseById: async (id: string): Promise<GetHouseByIdResponse> => {
    const response = await api.get<ApiResponse<GetHouseByIdResponse>>(
      `/houses/${id}`,
    );
    return response.data;
  },

  // Create house
  createHouse: async (data: CreateHouseData): Promise<House> => {
    return api.post<House>('/houses', data);
  },

  // Update house
  updateHouse: async (
    id: string,
    data: Partial<CreateHouseData>,
  ): Promise<House> => {
    return api.put<House>(`/houses/${id}`, data);
  },

  // Delete house
  deleteHouse: async (id: string): Promise<void> => {
    return api.delete<void>(`/houses/${id}`);
  },

  // Get house overview
  getHouseOverview: async (days: number = 30): Promise<HouseOverviewData> => {
    const response = await api.get<ApiResponse<HouseOverviewData>>(
      '/houses/overview',
      {
        params: { days },
      },
    );
    return response.data;
  },

  // Get house statistics
  // getHouseStats: async (id: string): Promise<HouseStats> => {
  //   return api.get<HouseStats>(`/houses/${id}/stats`);
  // },

  // Bulk operations for better UX
  // bulkUpdateHouses: async (
  //   updates: Array<{ id: string; data: Partial<CreateHouseData> }>,
  // ): Promise<House[]> => {
  //   return api.put<House[]>('/houses/bulk', { updates });
  // },

  // // Archive/unarchive house
  // archiveHouse: async (id: string): Promise<House> => {
  //   return api.patch<House>(`/houses/${id}/archive`);
  // },

  // unarchiveHouse: async (id: string): Promise<House> => {
  //   return api.patch<House>(`/houses/${id}/unarchive`);
  // },
  // Get upload URL for house media
  getUploadUrl: async (
    houseId: string,
    filename: string,
    contentType: string,
  ): Promise<{ signedUrl: string; token: string; path: string }> => {
    const response = await api.post<
      ApiResponse<{
        signedUrl: string;
        token: string;
        path: string;
      }>
    >('/houses/upload-url', { houseId, filename, contentType });
    return response.data;
  },
};

// Manager-specific API endpoints
// export const managerApi = {
//   // Get all house managers
//   getManagers: async (params?: {
//     page?: number;
//     limit?: number;
//     search?: string;
//     houseId?: string;
//   }): Promise<{
//     managers: HouseManager[];
//     pagination: {
//       page: number;
//       limit: number;
//       total: number;
//       pages: number;
//     };
//   }> => {
//     try {
//       const searchParams = new URLSearchParams();
//       if (params?.page) searchParams.append('page', params.page.toString());
//       if (params?.limit) searchParams.append('limit', params.limit.toString());
//       if (params?.search) searchParams.append('search', params.search);
//       if (params?.houseId) searchParams.append('houseId', params.houseId);

//       return api.get<{
//         managers: HouseManager[];
//         pagination: {
//           page: number;
//           limit: number;
//           total: number;
//           pages: number;
//         };
//       }>(`/managers?${searchParams.toString()}`);
//     } catch (error: any) {
//       throw new Error(
//         error.response?.data?.message || 'Failed to fetch managers',
//       );
//     }
//   },

//   // Get manager by ID
//   getManagerById: async (id: string): Promise<HouseManager> => {
//     return api.get<HouseManager>(`/managers/${id}`);
//   },

//   // Create manager assignment
//   assignManager: async (
//     data: CreateManagerAssignmentData,
//   ): Promise<HouseManager> => {
//     return api.post<HouseManager>('/managers/assign', data);
//   },

//   // Update manager assignments
//   updateManagerAssignments: async (
//     managerId: string,
//     data: UpdateManagerAssignmentData,
//   ): Promise<HouseManager> => {
//     return api.put<HouseManager>(`/managers/${managerId}/assignments`, data);
//   },

//   // Remove manager from house
//   removeManagerFromHouse: async (
//     managerId: string,
//     houseId: string,
//   ): Promise<void> => {
//     return api.delete<void>(`/managers/${managerId}/houses/${houseId}`);
//   },

//   // Remove manager completely
//   removeManager: async (managerId: string): Promise<void> => {
//     return api.delete<void>(`/managers/${managerId}`);
//   },

//   // Get manager statistics
//   getManagerStats: async (managerId: string): Promise<ManagerStats> => {
//     return api.get<ManagerStats>(`/managers/${managerId}/stats`);
//   },

//   // Get available users who can be managers
//   getAvailableManagers: async (): Promise<
//     Array<{
//       id: string;
//       firstName: string;
//       lastName: string;
//       email: string;
//       phone?: string;
//     }>
//   > => {
//     return api.get<
//       Array<{
//         id: string;
//         firstName: string;
//         lastName: string;
//         email: string;
//         phone?: string;
//       }>
//     >('/users?role=MANAGER&available=true');
//   },
// };

// Re-export types for convenience
export type {
  HouseManager,
  CreateManagerAssignmentData,
  UpdateManagerAssignmentData,
  ManagerStats,
} from '../types';
